import { restClient } from '../../client';
import { ValidationErrorDetails } from '../../errors';
import { TimeWindowResource } from '../../resources';

enum ProviderDisbursementBatchItemStatus {
  ready = 'ready',
  transferCreated = 'transfer_created',
  payoutCreated = 'payout_created',
  paid = 'paid',
  failed = 'failed',
}

export type ProviderDisbursementBatchItemResource = {
  id: string;
  providerId: string;
  amountCents: number;
  status: ProviderDisbursementBatchItemStatus;
  stripeTransferId: string | null;
  transferCreatedAt: string | null;
  stripePayoutId: string;
  payoutCreatedAt: string | null;
  errorMessage: string;
};

export enum ProviderDisbursementType {
  batch = 'batch',
  adjustment = 'adjustment',
}

export enum ProviderDisbursementBatchStatus {
  created = 'created',
  rejected = 'rejected',

  transfersReady = 'transfers_ready',
  transfersExecuting = 'transfers_executing',
  transfersCompleted = 'transfers_completed',
  transfersCompletedWithFailures = 'transfers_completed_with_failures',

  payoutsExecuting = 'payouts_executing',
  payoutsCompleted = 'payouts_completed',
  payoutsCompletedWithFailures = 'payouts_completed_with_failures',
  payoutsReconciling = 'payouts_reconciling',

  settledSuccess = 'settled_success',
  settledWithFailure = 'settled_with_failure',
}

export type ProviderDisbursementBatchResource = {
  id: string;
  uploadedBy: string;
  uploadedAt: string;
  createdAt: string;
  updatedAt: string;
  disbursementType: ProviderDisbursementType;
  status: ProviderDisbursementBatchStatus;
  payoutDate: string;
  periodStartDate: string;
  periodEndDate: string;
  totalAmountCents: number;
  totalProviders: number;
  items: ProviderDisbursementBatchItemResource[];

  rejectedAt: string | null;
  rejectedBy: string | null;
  rejectionReason: string | null;

  approvedAt: string | null;
  approvedBy: string | null;
} | (
    ProviderDisbursementBatchItemResource & {
      status: ProviderDisbursementBatchStatus.rejected;
      rejectedAt: string;
      rejectedBy: string;
      rejectionReason: string;
    })
  | (
    ProviderDisbursementBatchItemResource & {
      status: ProviderDisbursementBatchStatus.payoutsCompleted;
      approvedAt: string;
      approvedBy: string;
    });

export const getProviderDisbursementPayoutPeriod = () => restClient.request<{ data: TimeWindowResource }>('/api/v1/admin/provider_disbursements/payout_period');
export const getProviderDisbursementBatches = () => restClient.request<{ data: ProviderDisbursementBatchResource[] }>('/api/v1/admin/provider_disbursements/batches');
export const getProviderDisbursementBatch = (batchId: string) => restClient.request<{ data: ProviderDisbursementBatchResource }>(`/api/v1/admin/provider_disbursements/batches/${batchId}`);
export const approveProviderDisbursementBatch = (batchId: string) => restClient.request<{ data: { result: string } }>(`/api/v1/admin/provider_disbursements/batches/${batchId}/approve`, {
  method: 'POST',
});
export const rejectProviderDisbursementBatch = (batchId: string, rejectionReason: string) => restClient.request<{ data: { result: string } }>(`/api/v1/admin/provider_disbursements/batches/${batchId}/reject`, {
  method: 'POST',
  data: {
    rejection_reason: rejectionReason,
  },
});

export type ProviderDisbursementBatchExecutionResponse =
  { data: ProviderDisbursementBatchResource; error: null }
  | { error: { type: 'message'; message: string } }
  | { error: { type: 'missing_headers'; message: string; headers: string[] } }
  | { error: { type: 'fields_validation'; message: string; errors: ValidationErrorDetails[] } };

export const createProviderDisbursementBatchFromCSV = (params: {
  disbursementType: ProviderDisbursementType;
  payoutDate: string;
  file: File;
}) => restClient.request<ProviderDisbursementBatchExecutionResponse>('/api/v1/admin/provider_disbursements/from_csv', {
  method: 'POST',
  data: restClient.generateFormData({
    disbursement_type: params.disbursementType,
    payout_date: params.payoutDate,
    file: params.file,
  }),
});
